import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GuardianSidebar from '../components/GuardianSidebar';
import Toast, { useToast } from '../components/Toast';
import { useTheme } from '../context/ThemeContext';
import { useGuardianLanguage } from '../context/GuardianLanguageContext';
import translations from '../i18n/translations';
import DatePicker from '../components/DatePicker';
import { guardianFetch } from '../utils/authFetch';
import { logoutAndClearLocal } from '../api/authLogout';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const COUNTRY_OPTIONS = [
  { value: 'qa',    label_en: 'Qatar',        label_ar: 'قطر'                     },
  { value: 'sa',    label_en: 'Saudi Arabia', label_ar: 'المملكة العربية السعودية' },
  { value: 'ae',    label_en: 'UAE',          label_ar: 'الإمارات'                },
  { value: 'kw',    label_en: 'Kuwait',       label_ar: 'الكويت'                  },
  { value: 'bh',    label_en: 'Bahrain',      label_ar: 'البحرين'                 },
  { value: 'om',    label_en: 'Oman',         label_ar: 'عُمان'                   },
  { value: 'eg',    label_en: 'Egypt',        label_ar: 'مصر'                     },
  { value: 'jo',    label_en: 'Jordan',       label_ar: 'الأردن'                  },
  { value: 'other', label_en: 'Other',        label_ar: 'أخرى'                    },
];

function subjectScoreColor(score) {
  if (score >= 75) return '#22c55e';
  if (score >= 50) return '#f59e0b';
  return '#ef4444';
}

function SubjectBar({ name, score }) {
  const { T } = useTheme();
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(score), 80);
    return () => clearTimeout(t);
  }, [score]);
  const color = subjectScoreColor(score);
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <span style={{ fontSize: 12, color: T.textSecondary, maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>{score}%</span>
      </div>
      <div style={{ height: 5, background: 'rgba(150,150,150,0.18)', borderRadius: 99, overflow: 'hidden', position: 'relative' }}>
        <div style={{ height: '100%', width: `${width}%`, background: color, borderRadius: 99, transition: 'width 0.7s cubic-bezier(0.22,1,0.36,1)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.35) 50%,transparent 100%)', animation: 'gd-shine 2s ease-in-out infinite' }} />
        </div>
      </div>
    </div>
  );
}

function PinResetModal({ student, lang, T, onClose }) {
  const [phase,     setPhase]     = useState('confirm');
  const [loading,   setLoading]   = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [copied,    setCopied]    = useState(false);
  const [error,     setError]     = useState('');

  const handleConfirm = async () => {
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('jahbath_token') || '';
      const res   = await guardianFetch(`${API}/auth/generate-reset-code`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ student_id: student.id, guardian_token: token }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.status === 'success') {
        setResetCode(data.payload.reset_code);
        setPhase('code');
      } else {
        setError(lang === 'ar' ? 'حدث خطأ، حاول مجدداً' : 'An error occurred, please try again');
      }
    } catch {
      setError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Cannot connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(resetCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:20 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background:T.bgCardSolid, border:`1px solid ${T.border}`, borderRadius:18, padding:28, width:'100%', maxWidth:380, direction: lang === 'ar' ? 'rtl' : 'ltr' }}>
        {phase === 'confirm' && (
          <>
            <div style={{ fontSize:16, fontWeight:700, color:T.textPrimary, marginBottom:12 }}>
              🔑 {lang === 'ar' ? `إعادة تعيين PIN` : `Reset PIN`}
            </div>
            <div style={{ fontSize:14, color:T.textSecondary, marginBottom:20, lineHeight:1.6 }}>
              {lang === 'ar'
                ? `هل تريد إعادة تعيين رقم ${student.first_name} السري؟ سيتلقى كوداً جديداً صالحاً لمدة ٢٤ ساعة.`
                : `Reset ${student.first_name}'s PIN? They will receive a new code valid for 24 hours.`}
            </div>
            {error && <div style={{ color:'#ef4444', fontSize:13, marginBottom:12 }}>{error}</div>}
            <div style={{ display:'flex', gap:10 }}>
              <button
                onClick={handleConfirm}
                disabled={loading}
                style={{ flex:1, padding:'10px 0', background:'#059669', color:'white', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}
              >
                {loading ? '...' : (lang === 'ar' ? 'تأكيد' : 'Confirm')}
              </button>
              <button
                onClick={onClose}
                style={{ flex:1, padding:'10px 0', background:'transparent', color:T.textSecondary, border:`1px solid ${T.border}`, borderRadius:10, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}
              >
                {lang === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
            </div>
          </>
        )}

        {phase === 'code' && (
          <>
            <div style={{ fontSize:15, fontWeight:700, color:T.textPrimary, marginBottom:6, textAlign:'center' }}>
              {lang === 'ar' ? `الكود الجديد لـ ${student.first_name}:` : `New code for ${student.first_name}:`}
            </div>
            <div style={{ fontFamily:'"Sora",sans-serif', fontSize:'2.2rem', fontWeight:800, color:'#059669', letterSpacing:'0.3em', textAlign:'center', padding:'18px 0', background:'#F0FDF4', borderRadius:12, marginBottom:12, border:'1px solid #D1FAE5' }}>
              {resetCode}
            </div>
            <div style={{ fontSize:13, color:T.textSecondary, textAlign:'center', marginBottom:16 }}>
              {lang === 'ar' ? 'صالح لمدة ٢٤ ساعة — أرسله لابنك/ابنتك' : 'Valid for 24 hours — send it to your child'}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button
                onClick={handleCopy}
                style={{ flex:1, padding:'10px 0', background: copied ? '#D1FAE5' : '#059669', color: copied ? '#059669' : 'white', border: copied ? '1px solid #059669' : 'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all .2s' }}
              >
                {copied
                  ? (lang === 'ar' ? '✓ تم النسخ' : '✓ Copied')
                  : (lang === 'ar' ? 'نسخ الكود' : 'Copy code')}
              </button>
              <button
                onClick={onClose}
                style={{ flex:1, padding:'10px 0', background:'transparent', color:T.textSecondary, border:`1px solid ${T.border}`, borderRadius:10, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}
              >
                {lang === 'ar' ? 'إغلاق' : 'Close'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StudentCard({ student }) {
  const { T } = useTheme();
  const { lang } = useGuardianLanguage();
  const navigate = useNavigate();
  const tr = translations[lang];

  const ctx = student.session_context || null;
  const [showPinReset, setShowPinReset] = useState(false);

  const flag = student.country === 'qa' ? '🇶🇦' : '🌍';
  const subjects = student.subjects_performance || [];
  const hasData = subjects.length > 0;
  const visible = subjects.slice(0, 3);
  const extra = subjects.length - 3;

  const readinessColor = ctx?.readiness?.readiness_color
    ?? (!student.first_subject_id ? 'amber' : null);
  const readinessLabel = ctx?.readiness?.readiness_label
    ?? (!student.first_subject_id ? (lang === 'ar' ? 'لا توجد بيانات بعد' : 'No data yet') : null);

  const daysToExam = ctx?.exam_context?.days_to_exam;
  const examBanner = daysToExam !== null && daysToExam !== undefined && daysToExam <= 7;

  const readinessDotColors = { green: '#22c55e', amber: '#f59e0b', red: '#ef4444' };

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA' : 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      }).format(new Date(iso));
    } catch { return iso?.slice(0, 10); }
  };

  return (
    <div style={{
      background: T.bgCardSolid, border: `1px solid ${T.border}`,
      borderRadius: 14, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      transition: 'background 0.2s ease',
    }}>
      <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.textPrimary }}>{student.first_name}</div>
            <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 3 }}>
              {flag} {lang === 'ar' ? `الصف ${student.grade}` : `Grade ${student.grade}`}
            </div>
          </div>
          {readinessColor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <div style={{ width: 9, height: 9, borderRadius: '50%', background: readinessDotColors[readinessColor] || readinessDotColors.amber }} />
              <span style={{ fontSize: 11, color: T.textSecondary }}>{readinessLabel}</span>
            </div>
          )}
        </div>

        {student.last_session_date && (
          <div style={{ fontSize: 12, color: T.textSecondary }}>
            {tr.lastSession}: {formatDate(student.last_session_date)}
          </div>
        )}

        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
          {!hasData ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 12, color: T.textSecondary, fontStyle: 'italic' }}>{tr.noSessionsYet}</span>
              <button
                onClick={() => navigate(`/guardian/student/${student.id}`)}
                style={{ padding: '4px 12px', background: `${T.accent}18`, border: `1px solid ${T.accent}50`, color: T.accent, borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
              >
                {tr.startNow}
              </button>
            </div>
          ) : (
            <>
              {visible.map((s, i) => (
                <SubjectBar key={i} name={s.subject_name} score={Math.round(s.avg_correct_percentage)} />
              ))}
              {extra > 0 && (
                <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2, textAlign: lang === 'ar' ? 'right' : 'left' }}>
                  {lang === 'ar' ? `و${extra} مواد أخرى` : `and ${extra} more`}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display:'flex', gap:8, marginTop:2 }}>
          <button
            onClick={() => navigate(`/guardian/student/${student.id}`)}
            style={{ flex:1, padding: '8px 0', background: `${T.accent}18`, border: `1px solid ${T.accent}40`, color: T.accent, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {tr.viewDetails}
          </button>
          <button
            onClick={() => setShowPinReset(true)}
            style={{ padding: '8px 10px', background: 'white', border: '1px solid #D1FAE5', color: '#059669', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F0FDF4'}
            onMouseLeave={e => e.currentTarget.style.background = 'white'}
          >
            🔑 {lang === 'ar' ? 'إعادة PIN' : 'Reset PIN'}
          </button>
        </div>
      </div>

      {examBanner && (
        <div style={{ background: '#f59e0b', padding: '7px 20px', fontSize: 12, fontWeight: 600, color: '#7c2d12' }}>
          {lang === 'ar' ? `امتحان بعد ${daysToExam} أيام` : `Exam in ${daysToExam} days`}
        </div>
      )}

      {showPinReset && (
        <PinResetModal
          student={student}
          lang={lang}
          T={T}
          onClose={() => setShowPinReset(false)}
        />
      )}
    </div>
  );
}

/* ──────────────── overview panel helpers ──────────────── */

const FEED_CFG = {
  warning:           { color: '#ef4444', icon: '⏰' },
  exam:              { color: '#f97316', icon: '📅' },
  weakness:          { color: '#f59e0b', icon: '⚠️' },
  achievement:       { color: '#22c55e', icon: '🔥' },
  progress:          { color: '#059669', icon: '📈' },
  unit_completed:    { color: '#22c55e', icon: '✅' },
  all_good:          { color: '#059669', icon: '🌟' },
  // ── DB notification_type mappings ─────────────────────────────────
  repeated_weakness: { color: '#f59e0b', icon: '⚠️' },
  missed_sessions:   { color: '#ef4444', icon: '⏰' },
  streak_warning:    { color: '#ef4444', icon: '⏰' },
  level_upgrade:     { color: '#22c55e', icon: '🔥' },
};

const FEED_ACTIONS = {
  ar: { warning: 'تذكير بالمذاكرة', exam: 'عرض خطة المراجعة', weakness: 'عرض نقاط الضعف', achievement: 'مشاركة الإنجاز', progress: 'عرض التقرير', unit_completed: 'عرض تقرير الجلسة' },
  en: { warning: 'Send Reminder',    exam: 'View Review Plan',  weakness: 'View Weaknesses',  achievement: 'Share Achievement', progress: 'View Report', unit_completed: 'View Session Report' },
};

const getFeedItem = (item, isAr) => ({
  type: item.notification_type || item.type,
  title: item.type === 'all_good'
    ? (isAr ? 'كل شيء على ما يرام!' : 'Everything looks great!')
    : (isAr ? item.message_ar : item.message_en),
  time: item.triggered_at
    ? new Date(item.triggered_at).toLocaleDateString(
        isAr ? 'ar-QA' : 'en-US',
        { month: 'short', day: 'numeric' }
      )
    : '',
});

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

/* ─────────────────────────────── FeedItem ──────────────────────────── */

function FeedItem({ item, lang, T, studentId, studentName, onDismiss, navigate }) {
  const _fi       = getFeedItem(item, lang === 'ar');
  const cfg       = FEED_CFG[_fi.type] || FEED_CFG.all_good;
  const isAllGood = _fi.type === 'all_good';
  const action    = (FEED_ACTIONS[lang] || FEED_ACTIONS.en)[_fi.type];
  const dir       = lang === 'ar' ? 'rtl' : 'ltr';

  return (
    <div style={{
      background: T.bgCardSolid, border: `1.5px solid ${cfg.color}35`,
      borderRadius: 12, padding: '14px 16px', marginBottom: 10,
      position: 'relative', direction: dir,
    }}>
      {!isAllGood && onDismiss && (
        <button onClick={onDismiss} style={{
          position: 'absolute', top: 8, [lang === 'ar' ? 'left' : 'right']: 8,
          background: 'none', border: 'none', color: T.textSecondary,
          fontSize: 15, cursor: 'pointer', padding: '2px 6px', lineHeight: 1,
        }}>×</button>
      )}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
          background: `${cfg.color}18`, fontSize: 17,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{cfg.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 3 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: T.textPrimary, paddingInlineEnd: isAllGood ? 0 : 16 }}>
              {_fi.title}
            </div>
            {_fi.time && (
              <span style={{ fontSize: 11, color: T.textSecondary, flexShrink: 0, whiteSpace: 'nowrap' }}>
                {_fi.time}
              </span>
            )}
          </div>
          {action && (
            <button
              onClick={() => navigate(`/guardian/student/${studentId}`)}
              style={{
                marginTop: 10, padding: '5px 12px', borderRadius: 8,
                background: 'none', border: `1.5px solid ${cfg.color}`,
                color: cfg.color, fontSize: 12, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >{action}</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── ChallengeStrip ───────────────────────── */

function ChallengeStrip({ challenges, isAr, loading }) {
  if (loading) return null;
  if (!challenges || challenges.length === 0) return null;

  const PHASE_COLOR = {
    phase1: '#3B82F6',
    phase2: '#D97706',
    phase3: '#22C55E',
  };

  return (
    <div style={{ margin: '8px 0 4px', padding: '0 4px' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', marginBottom: 6, textAlign: isAr ? 'right' : 'left' }}>
        {isAr ? '🎯 التحديات النشطة' : '🎯 Active Challenges'}
      </div>
      {challenges.map(ch => {
        const color = PHASE_COLOR[ch.phase] || '#6B7280';
        const pct   = ch.progress_pct || 0;
        return (
          <div key={ch.id} style={{ background: '#F9FAFB', border: `1px solid ${color}33`, borderRadius: 10, padding: '8px 12px', marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#1F2937' }}>{ch.weakness_name}</span>
              <span style={{ fontSize: 11, color: '#6B7280' }}>{ch.subject_name}</span>
            </div>
            <div style={{ background: '#E5E7EB', borderRadius: 4, height: 6 }}>
              <div style={{ width: `${pct}%`, background: color, borderRadius: 4, height: 6, transition: 'width 0.3s' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ fontSize: 11, color: '#6B7280' }}>
                {ch.sessions_done}/{ch.sessions_required}{isAr ? ' جلسات' : ' sessions'}
              </span>
              <span style={{ fontSize: 11, color: '#6B7280' }}>
                {ch.avg_score != null && ch.sessions_done > 0
                  ? `${isAr ? 'المتوسط:' : 'Avg:'} ${ch.avg_score}% → ${isAr ? 'الهدف:' : 'Target:'} ${ch.target_score}%`
                  : `${isAr ? 'الهدف:' : 'Target:'} ${ch.target_score}%`
                }
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────── StudentPanel ─────────────────────────── */

function StudentPanel({ student, panelStyle, lang, T, navigate, dismissedIds, onDismiss, onSetChallenge, token }) {
  const rawFeed    = (student.feed || []).map((item, i) => ({
    ...item,
    type: item.notification_type || item.type || 'all_good',
    _idx: i,
  }));
  const visible    = rawFeed.filter(item => !dismissedIds.includes(item._idx));
  const displayFeed = visible.length > 0 ? visible : [{ type: 'all_good', _idx: -1 }];

  const overallAvg   = Math.round(student.overall_avg || student.avg_correct_percentage || 0);
  const change       = null;

  const daysInactive = student.last_session_date
    ? Math.floor(
        (Date.now() - new Date(student.last_session_date).getTime())
        / (1000 * 60 * 60 * 24)
      )
    : null;

  const dotColor = daysInactive < 1 ? '#22c55e' : daysInactive <= 3 ? '#f59e0b' : '#ef4444';
  const dotText  = lang === 'ar'
    ? daysInactive < 1 ? 'نشط اليوم' : daysInactive === 1 ? 'نشط منذ يوم' : daysInactive <= 3 ? `نشط منذ ${daysInactive} أيام` : 'غير نشط'
    : daysInactive < 1 ? 'Active today' : daysInactive === 1 ? 'Active 1 day ago' : daysInactive <= 3 ? `Active ${daysInactive} days ago` : 'Inactive';

  const [barW, setBarW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setBarW(overallAvg), 150);
    return () => clearTimeout(t);
  }, [overallAvg]);

  const challenges = student.challenges || [];

  const barColor = subjectScoreColor(overallAvg);
  const dir      = lang === 'ar' ? 'rtl' : 'ltr';

  return (
    <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column', minHeight: '70vh' }}>
      <div style={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', padding: '16px 20px', flexShrink: 0, borderBottom: `2px solid ${T.accent}22`, direction: dir }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.textPrimary }}>{student.first_name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
              <span style={{ background: `${T.accent}18`, color: T.accent, border: `1px solid ${T.accent}40`, borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                {lang === 'ar' ? `الصف ${student.grade}` : `Grade ${student.grade}`}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: T.textSecondary }}>{dotText}</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => navigate(`/guardian/student/${student.id}`)}
              style={{ background: 'none', border: `1px solid ${T.border}`, color: T.textSecondary, borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}
            >{lang === 'ar' ? 'عرض التفاصيل' : 'View Details'}</button>
            <button
              onClick={() => onSetChallenge({ student })}
              style={{ background: 'linear-gradient(135deg,#D97706,#F59E0B)', color: 'white', border: 'none', borderRadius: 10, padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
            >{lang === 'ar' ? '🎯 تحدي' : '🎯 Challenge'}</button>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 4, background: 'rgba(150,150,150,0.18)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${barW}%`, background: barColor, borderRadius: 99, transition: 'width 0.8s cubic-bezier(0.22,1,0.36,1)' }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: barColor }}>{overallAvg}%</span>
          {change !== null && (
            <span style={{ fontSize: 12, fontWeight: 700, color: change >= 0 ? '#22c55e' : '#ef4444', whiteSpace: 'nowrap' }}>
              {change >= 0 ? '▲' : '▼'} {Math.abs(change)}%
            </span>
          )}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: T.bg, direction: dir }}>
        <ChallengeStrip challenges={challenges} isAr={lang === 'ar'} loading={false} />
        {displayFeed.map(item => (
          <FeedItem
            key={item._idx}
            item={item}
            lang={lang}
            T={T}
            studentId={student.id}
            studentName={student.first_name}
            onDismiss={item._idx >= 0 ? () => onDismiss(item._idx) : undefined}
            navigate={navigate}
          />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────── OverviewPanels ─────────────────────── */

function OverviewPanels({ students, lang, navigate, onSetChallenge, token }) {
  const { T }   = useTheme();
  const [dismissed, setDismissed] = useState({});
  const windowWidth = useWindowWidth();
  const isMobile    = windowWidth < 768;

  const onDismiss = (sid, idx) =>
    setDismissed(d => ({ ...d, [sid]: [...(d[sid] || []), idx] }));

  const n          = students.length;
  const mainGroup  = students.slice(0, Math.min(n, 4));
  const extraGroup = n > 4 ? students.slice(4) : [];
  const cols       = isMobile ? 1 : n === 1 ? 1 : n <= 3 ? n : 2;
  const mainTotal  = mainGroup.length;

  const getPanelStyle = (i) => {
    const isLastCol = (i + 1) % cols === 0;
    const totalRows = Math.ceil(mainTotal / cols);
    const isLastRow = Math.floor(i / cols) === totalRows - 1;
    return {
      width: `${100 / cols}%`,
      boxSizing: 'border-box',
      borderInlineEnd: !isLastCol ? `1px solid ${T.border}` : 'none',
      borderBottom:    !isLastRow ? `1px solid ${T.border}` : 'none',
    };
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
        {mainGroup.map((s, i) => (
          <StudentPanel
            key={s.id}
            student={s}
            panelStyle={getPanelStyle(i)}
            lang={lang}
            T={T}
            navigate={navigate}
            dismissedIds={dismissed[s.id] || []}
            onDismiss={(idx) => onDismiss(s.id, idx)}
            onSetChallenge={onSetChallenge}
            token={token}
          />
        ))}
      </div>
      {extraGroup.length > 0 && (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', marginTop: 16 }}>
          <div style={{ display: 'flex', overflowX: 'auto' }}>
            {extraGroup.map((s, i) => (
              <StudentPanel
                key={s.id}
                student={s}
                panelStyle={{ minWidth: 300, flex: '0 0 auto', boxSizing: 'border-box', borderInlineEnd: i < extraGroup.length - 1 ? `1px solid ${T.border}` : 'none' }}
                lang={lang}
                T={T}
                navigate={navigate}
                dismissedIds={dismissed[s.id] || []}
                onDismiss={(idx) => onDismiss(s.id, idx)}
                onSetChallenge={onSetChallenge}
                token={token}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── FamilyOverview ───────────────────────── */

function FamilyOverview({ students }) {
  const { T } = useTheme();
  const { lang } = useGuardianLanguage();
  const tr = translations[lang];
  const dotColors = { green: '#22c55e', amber: '#f59e0b', red: '#ef4444' };

  return (
    <div style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, marginBottom: 12 }}>{tr.familyOverview}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {students.map(s => {
          const avg = s.overall_avg || 0;
          const dotColor = avg >= 75 ? 'green' : avg >= 50 ? 'amber' : 'red';
          return (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColors[dotColor], flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: T.textPrimary, fontWeight: 600, flex: 1 }}>{s.first_name}</span>
              <span style={{ fontSize: 13, color: subjectScoreColor(avg), fontWeight: 700 }}>{avg}%</span>
              <span style={{ fontSize: 12, color: T.textSecondary }}>
                {lang === 'ar' ? `الصف ${s.grade}` : `Grade ${s.grade}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AddStudentModal({ guardianId, onClose, onAdded }) {
  const { T } = useTheme();
  const { lang } = useGuardianLanguage();
  const tr = translations[lang];
  const [form, setForm] = useState({
    student_name: '', student_grade: '7', student_country: 'qa',
    school_name: '', ict_or_cs: '',
    islamic_education_lang: '', arabic_lang: '', qatar_history_lang: '',
    midterm_exam_date: '', final_exam_date: '',
  });
  const [monthlyDates, setMonthlyDates] = useState([]);
  const [dateErrors,   setDateErrors]   = useState({});
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [invCode,  setInvCode]  = useState('');
  const [copied,   setCopied]   = useState(false);
  const grade = parseInt(form.student_grade, 10);
  const set   = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateDates = () => {
    const errs = {};
    if (!form.midterm_exam_date) errs.midterm = tr.required;
    if (!form.final_exam_date)   errs.final   = tr.required;
    if (form.midterm_exam_date && form.final_exam_date && form.final_exam_date <= form.midterm_exam_date) {
      errs.final = tr.dateOrderError;
    }
    const monthly = {};
    monthlyDates.forEach((d, i) => {
      if (d && form.midterm_exam_date && form.final_exam_date) {
        if (d <= form.midterm_exam_date || d >= form.final_exam_date) {
          monthly[i] = tr.monthlyDateRangeError;
        }
      }
    });
    if (Object.keys(monthly).length) errs.monthly = monthly;
    return errs;
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px',
    background: T.bgInput, border: `1px solid ${T.border}`,
    borderRadius: 10, color: T.textPrimary, fontSize: 14,
    boxSizing: 'border-box', marginBottom: 14, outline: 'none', fontFamily: 'inherit',
  };

  const handleSubmit = async () => {
    if (!form.student_name.trim()) { setError(lang === 'ar' ? 'اسم الطالب مطلوب' : 'Student name required'); return; }
    const errs = validateDates();
    if (Object.keys(errs).length) { setDateErrors(errs); return; }
    setLoading(true); setError(''); setDateErrors({});
    try {
      const res = await guardianFetch(`${API}/guardian/add-student`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guardian_id: guardianId, student_name: form.student_name.trim(),
          student_grade: grade, student_country: form.student_country,
          school_name: form.school_name.trim() || null,
          ict_or_cs: (grade >= 7 && grade <= 9) ? (form.ict_or_cs || null) : null,
          islamic_education_lang: form.islamic_education_lang || null,
          arabic_lang: form.arabic_lang || null,
          qatar_history_lang: form.qatar_history_lang || null,
          midterm_exam_date: form.midterm_exam_date || null,
          final_exam_date:   form.final_exam_date   || null,
          monthly_exam_dates: monthlyDates.filter(d => d),
        }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.status === 'success' || data.success) {
        setInvCode(data.payload?.invitation_code || '');
      }
      else { setError(lang === 'ar' ? 'حدث خطأ أثناء إضافة الطالب' : 'Error adding student'); }
    } catch {
      setError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Cannot connect to server');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 18, padding: 32, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto' }}>
        {invCode ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.textPrimary, marginBottom: 20 }}>
              {lang === 'ar' ? 'تمت إضافة الطالب ✓' : 'Student Added ✓'}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 34, fontWeight: 800, letterSpacing: 8, color: T.accent, background: `${T.accent}14`, border: `2px solid ${T.accent}40`, borderRadius: 14, padding: '18px 24px', marginBottom: 20, display: 'inline-block' }}>
              {invCode}
            </div>
            <div style={{ fontSize: 14, color: T.textSecondary, marginBottom: 24, lineHeight: 1.7, direction: 'rtl' }}>
              أرسل هذا الرمز للطالب ليتمكن من التسجيل — صالح لمدة 72 ساعة
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { navigator.clipboard.writeText(invCode); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                style={{ flex: 1, padding: '11px 0', background: 'transparent', color: T.accent, border: `1px solid ${T.accent}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {copied ? '✓' : (lang === 'ar' ? 'نسخ الرمز' : 'Copy')}
              </button>
              <button
                onClick={onAdded}
                style={{ flex: 1, padding: '11px 0', background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', borderRadius: 12, boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {lang === 'ar' ? 'تم' : 'Done'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 19, fontWeight: 700, color: T.textPrimary, marginBottom: 20 }}>
              {lang === 'ar' ? 'إضافة طالب جديد' : 'Add New Student'}
            </div>
        {error && <div style={{ background: `${T.error}18`, border: `1px solid ${T.error}`, color: T.error, borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>{error}</div>}

        <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6, display: 'block' }}>{lang === 'ar' ? 'اسم الطالب' : 'Student Name'}</label>
        <input style={inputStyle} dir="rtl" value={form.student_name} onChange={e => set('student_name', e.target.value)} />

        <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6, display: 'block' }}>{lang === 'ar' ? 'الصف الدراسي' : 'Grade'}</label>
        <select style={inputStyle} value={form.student_grade} onChange={e => set('student_grade', e.target.value)}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(g => <option key={g} value={g}>{lang === 'ar' ? `الصف ${g}` : `Grade ${g}`}</option>)}
        </select>

        <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6, display: 'block' }}>{lang === 'ar' ? 'الدولة' : 'Country'}</label>
        <select style={inputStyle} value={form.student_country} onChange={e => set('student_country', e.target.value)}>
          {COUNTRY_OPTIONS.map(c => <option key={c.value} value={c.value}>{lang === 'ar' ? c.label_ar : c.label_en}</option>)}
        </select>

        <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6, display: 'block' }}>{lang === 'ar' ? 'اسم المدرسة (اختياري)' : 'School Name (optional)'}</label>
        <input style={inputStyle} dir="rtl" value={form.school_name} onChange={e => set('school_name', e.target.value)} />

        {[
          { key: 'islamic_education_lang', label: { ar: 'لغة التربية الإسلامية', en: 'Islamic Education Language' } },
          { key: 'arabic_lang',            label: { ar: 'لغة اللغة العربية',      en: 'Arabic Language'           } },
          { key: 'qatar_history_lang',     label: { ar: 'لغة تاريخ قطر',         en: 'Qatar History Language'    } },
        ].map(f => (
          <React.Fragment key={f.key}>
            <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6, display: 'block' }}>{f.label[lang]}</label>
            <select style={inputStyle} value={form[f.key]} onChange={e => set(f.key, e.target.value)}>
              <option value="">— {lang === 'ar' ? 'اختر' : 'Choose'} —</option>
              <option value="ar">{lang === 'ar' ? 'عربي' : 'Arabic'}</option>
              <option value="en">{lang === 'ar' ? 'إنجليزي' : 'English'}</option>
            </select>
          </React.Fragment>
        ))}

        {grade >= 7 && grade <= 9 && (
          <>
            <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6, display: 'block' }}>
              {lang === 'ar' ? 'المسار (ICT أو علوم الحاسوب)' : 'Track (ICT or Computer Science)'}
            </label>
            <select style={inputStyle} value={form.ict_or_cs} onChange={e => set('ict_or_cs', e.target.value)}>
              <option value="">— {lang === 'ar' ? 'لا ينطبق' : 'Not applicable'} —</option>
              <option value="ict">ICT</option>
              <option value="cs">{lang === 'ar' ? 'علوم الحاسوب' : 'Computer Science'}</option>
            </select>
          </>
        )}

        <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 8, paddingTop: 20, marginBottom: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.accent, marginBottom: 16 }}>{tr.examDates}</div>

          <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6, display: 'block' }}>
            {tr.midterm} <span style={{ color: T.error }}>*</span>
          </label>
          <DatePicker
            value={form.midterm_exam_date}
            onChange={v => set('midterm_exam_date', v)}
            placeholder={lang === 'ar' ? 'اختر تاريخاً' : 'Select a date'}
          />
          {dateErrors.midterm && <div style={{ fontSize: 12, color: T.error, marginTop: -10, marginBottom: 10 }}>{dateErrors.midterm}</div>}

          <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6, display: 'block' }}>
            {tr.finalExam} <span style={{ color: T.error }}>*</span>
          </label>
          <DatePicker
            value={form.final_exam_date}
            onChange={v => set('final_exam_date', v)}
            placeholder={lang === 'ar' ? 'اختر تاريخاً' : 'Select a date'}
          />
          {dateErrors.final && <div style={{ fontSize: 12, color: T.error, marginTop: -10, marginBottom: 10 }}>{dateErrors.final}</div>}

          <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 10, fontWeight: 500 }}>{tr.monthlyTests}</div>
          {monthlyDates.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <DatePicker
                  value={d}
                  onChange={v => {
                    const next = [...monthlyDates];
                    next[i] = v;
                    setMonthlyDates(next);
                  }}
                  placeholder={lang === 'ar' ? 'اختر تاريخاً' : 'Select a date'}
                  wrapperStyle={{ marginBottom: 0 }}
                />
                {dateErrors.monthly?.[i] && (
                  <div style={{ fontSize: 12, color: T.error, marginTop: 4 }}>{dateErrors.monthly[i]}</div>
                )}
              </div>
              <button
                onClick={() => setMonthlyDates(monthlyDates.filter((_, j) => j !== i))}
                style={{ padding: '8px 12px', background: 'transparent', border: `1px solid ${T.error}`, color: T.error, borderRadius: 8, cursor: 'pointer', fontSize: 14, fontFamily: 'inherit', flexShrink: 0 }}
              >✕</button>
            </div>
          ))}
          {monthlyDates.length < 12 && (
            <button
              onClick={() => setMonthlyDates([...monthlyDates, ''])}
              style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${T.border}`, color: T.accent, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}
            >{tr.addDate}</button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={handleSubmit} disabled={loading} style={{ flex: 1, padding: '11px 0', background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', borderRadius: 12, boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            {loading ? '...' : (lang === 'ar' ? 'إضافة' : 'Add')}
          </button>
          <button onClick={onClose} style={{ flex: 1, padding: '11px 0', background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
            {tr.cancel}
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
}

const REPORT_TYPE_LABEL = { ar: { session_report: 'تقرير جلسة', weekly_report: 'تقرير أسبوعي', monthly_report: 'تقرير شهري', exam_report: 'تقرير امتحان' }, en: { session_report: 'Session Report', weekly_report: 'Weekly Report', monthly_report: 'Monthly Report', exam_report: 'Exam Report' } };

/* ──────────────────────────── ChallengeModal ───────────────────────── */

function ChallengeModal({ student, lang, token, onClose, onCreated }) {
  const [subjectId,    setSubjectId]    = useState('');
  const [weaknessName, setWeaknessName] = useState('');
  const [targetScore,  setTargetScore]  = useState(70);
  const [days,         setDays]         = useState(14);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');
  const [unitsData,    setUnitsData]    = useState({});
  const [unitId,       setUnitId]       = useState('');
  const [topicName,    setTopicName]    = useState('');
  const [loadingUnits, setLoadingUnits] = useState(false);

  useEffect(() => {
    if (!subjectId || !student?.id) return;
    setUnitId('');
    setTopicName('');
    setWeaknessName('');
    setLoadingUnits(true);
    guardianFetch(`${API}/guardian/student-subjects/${student.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r ? r.json() : null)
      .then(d => { if (d && d.success) setUnitsData(d.units_by_subject || {}); })
      .catch(() => {})
      .finally(() => setLoadingUnits(false));
  }, [subjectId, student?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAr     = lang === 'ar';
  const subjects = student.subjects_performance || [];

  const handleSubmit = async () => {
    if (!subjectId || !unitId || !weaknessName.trim()) {
      setError(isAr
        ? 'برجاء اختيار المادة والوحدة والموضوع'
        : 'Please select a subject, unit, and topic');
      return;
    }
    setSaving(true);
    try {
      const res = await guardianFetch(`${API}/guardian/challenge`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          student_id:        student.id,
          subject_id:        subjectId,
          weakness_name:     weaknessName.trim(),
          target_score:      targetScore,
          sessions_required: 3,
          expires_days:      days,
          unit_id:           unitId,
          unit_name:         (unitsData[subjectId] || []).find(u => u.unit_id === unitId)?.unit_name || '',
          subject_name:      subjects.find(s => s.subject_id === subjectId)?.subject_name || '',
        }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        onCreated();
        onClose();
      } else {
        setError(data.errors?.[0]?.message || data.error || 'Error');
      }
    } catch {
      setError(isAr ? 'خطأ في الاتصال' : 'Connection error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 18, padding: 28, width: 400, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', direction: isAr ? 'rtl' : 'ltr' }}>
        <h3 style={{ margin: '0 0 20px', fontFamily: 'Sora,sans-serif', color: '#1E3A5F' }}>
          {isAr ? `🎯 تحدي تحسين لـ ${student.first_name}` : `🎯 Set Challenge for ${student.first_name}`}
        </h3>

        <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>
          {isAr ? 'المادة' : 'Subject'}
        </label>
        <select
          value={subjectId}
          onChange={e => setSubjectId(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', borderRadius: 10, border: '1px solid #E5E7EB', marginBottom: 16, fontSize: 14 }}
        >
          <option value="">{isAr ? '-- اختر مادة --' : '-- Select subject --'}</option>
          {subjects.map(s => (
            <option key={s.subject_id || s.subject_name} value={s.subject_id || ''}>
              {s.subject_name}
            </option>
          ))}
        </select>

        <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>
          {isAr ? 'الوحدة' : 'Unit'}
        </label>
        <select
          value={unitId}
          onChange={e => { setUnitId(e.target.value); setTopicName(''); setWeaknessName(''); }}
          disabled={!subjectId || loadingUnits}
          style={{ width: '100%', padding: '8px 12px', borderRadius: 10, border: '1px solid #E5E7EB', marginBottom: 16, fontSize: 14 }}
        >
          <option value="">
            {loadingUnits
              ? (isAr ? 'جارٍ التحميل...' : 'Loading...')
              : (isAr ? '-- اختر وحدة --' : '-- Select unit --')}
          </option>
          {(unitsData[subjectId] || []).map(u => (
            <option key={u.unit_id} value={u.unit_id}>
              {u.unit_name}{u.semester ? ` (S${u.semester})` : ''}
            </option>
          ))}
        </select>

        {unitId && (
          <>
            <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>
              {isAr ? 'الموضوع' : 'Topic'}
            </label>
            <select
              value={topicName}
              onChange={e => { setTopicName(e.target.value); setWeaknessName(e.target.value); }}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 10, border: '1px solid #E5E7EB', marginBottom: 16, fontSize: 14 }}
            >
              <option value="">{isAr ? '-- اختر موضوعاً --' : '-- Select topic --'}</option>
              {((unitsData[subjectId] || []).find(u => u.unit_id === unitId)?.topics || []).map((t, i) => (
                <option key={i} value={t.topic_name}>{t.topic_name}</option>
              ))}
            </select>
          </>
        )}

        <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>
          {isAr ? `الهدف: ${targetScore}%` : `Target Score: ${targetScore}%`}
        </label>
        <input type="range" min={50} max={100} step={5}
          value={targetScore}
          onChange={e => setTargetScore(Number(e.target.value))}
          style={{ width: '100%', marginBottom: 16 }}
        />

        <label style={{ fontSize: 13, color: '#6B7280', display: 'block', marginBottom: 6 }}>
          {isAr ? `المدة: ${days} يوم` : `Duration: ${days} days`}
        </label>
        <input type="range" min={7} max={30} step={7}
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          style={{ width: '100%', marginBottom: 20 }}
        />

        {error && <div style={{ color: '#EF4444', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer', fontSize: 14 }}
          >
            {isAr ? 'إلغاء' : 'Cancel'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#D97706,#F59E0B)', color: 'white', cursor: 'pointer', fontSize: 14, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? (isAr ? 'جارٍ الحفظ...' : 'Saving...') : (isAr ? 'حفظ التحدي' : 'Save Challenge')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GuardianDashboard() {
  const navigate   = useNavigate();
  const guardianId = localStorage.getItem('jahbath_guardian_id');
  const token      = localStorage.getItem('jahbath_token');
  const { T } = useTheme();
  const { lang } = useGuardianLanguage();
  const tr = translations[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const { toast, show: showToast } = useToast();

  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [tab,            setTab]            = useState('overview');
  const [showAdd,        setShowAdd]        = useState(false);
  const [challengeModal, setChallengeModal] = useState(null);

  useEffect(() => {
    if (!token || !guardianId) { navigate('/guardian/auth', { replace: true }); return; }
    loadDashboard();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboard = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res  = await guardianFetch(`${API}/guardian/dashboard/${guardianId}`);
      if (!res) return;
      const json = await res.json();
      if (json.success) { setData(json); }
      else { setError(json.error || (lang === 'ar' ? 'تعذر تحميل البيانات' : 'Failed to load data')); }
    } catch {
      setError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Cannot connect to server');
    } finally { setLoading(false); }
  }, [guardianId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await logoutAndClearLocal('guardian');
    navigate('/guardian/auth', { replace: true });
  };

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  const tabStyle = (active) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: 14,
    fontWeight: active ? 600 : 400, color: active ? T.accent : T.textSecondary,
    borderBottom: active ? `2px solid ${T.accent}` : '2px solid transparent',
    background: 'transparent', border: 'none',
    borderBottom: active ? `2px solid ${T.accent}` : '2px solid transparent',
    marginBottom: -1, transition: 'all 0.2s ease', fontFamily: 'inherit',
  });

  const card = { background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', border: '1px solid rgba(229,231,235,0.8)', borderRadius: 18, padding: 20, boxShadow: '0 4px 20px rgba(5,150,105,0.10)', transition: 'background 0.2s ease' };
  const statCard = { background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', border: '1px solid rgba(229,231,235,0.8)', borderRadius: 18, padding: '18px 20px', boxShadow: '0 4px 20px rgba(5,150,105,0.10)' };

  if (!token || !guardianId) return null;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'transparent', color: T.textPrimary, fontFamily: "'Sora', sans-serif", direction: dir, transition: 'background 0.2s ease' }}>
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;600;700;800&display=swap');
        @keyframes gd-shine { 0%,100%{transform:translateX(-100%)} 50%{transform:translateX(100%)} }
        @media (max-width: 767px) {
          main { width: 100% !important; padding: 12px !important; margin-left: 0 !important; margin-right: 0 !important; box-sizing: border-box !important; }
          main [style*="font-size: 26px"] { font-size: 18px !important; }
          [style*="grid-template-columns"] { grid-template-columns: 1fr !important; }
          [style*="inset: 0"] > div { width: 95vw !important; max-width: 95vw !important; padding: 16px !important; margin: 10px auto !important; box-sizing: border-box !important; }
          table { display: block !important; overflow-x: auto !important; }
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          main { padding: 20px !important; }
          [style*="grid-template-columns"] { grid-template-columns: repeat(2, 1fr) !important; }
          [style*="inset: 0"] > div { max-width: 680px !important; }
        }
      `}</style>
      <GuardianSidebar guardianName={data?.guardian?.first_name} guardianEmail={data?.guardian?.email} onLogout={handleLogout} />

      <main style={{ flex: 1, padding: '32px 36px', overflowY: 'auto', position: 'relative', zIndex: 10 }}>
        {loading ? (
          <>
            <style>{`@keyframes gd-pulse{0%,100%{opacity:.4}50%{opacity:.8}}.gd-skel{background:${T.border};border-radius:10px;animation:gd-pulse 1.6s ease-in-out infinite}`}</style>
            <div className="gd-skel" style={{ height: 34, width: 240, marginBottom: 8 }} />
            <div className="gd-skel" style={{ height: 16, width: 180, marginBottom: 32 }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 16, marginBottom: 28 }}>
              {[...Array(3)].map((_, i) => <div key={i} className="gd-skel" style={{ height: 90, borderRadius: 14 }} />)}
            </div>
            <div className="gd-skel" style={{ height: 280, borderRadius: 14 }} />
          </>
        ) : error ? (
          <div style={{ textAlign: 'center', paddingTop: 80 }}>
            <div style={{ color: T.error, fontSize: 16, marginBottom: 16 }}>{error}</div>
            <button onClick={loadDashboard} style={{ padding: '11px 28px', background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', borderRadius: 12, boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{tr.retryBtn}</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 26, fontWeight: 700, color: T.textPrimary, marginBottom: 4 }}>
              {tr.welcome}، {data.guardian.first_name}
            </div>
            <div style={{ fontSize: 14, color: T.textSecondary, marginBottom: 28 }}>
              {lang === 'ar' ? 'إليك ملخص أداء أبنائك' : "Here's a summary of your children's performance"}
            </div>

            <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${T.border}`, paddingBottom: 0 }}>
              <button style={tabStyle(tab === 'overview')} onClick={() => setTab('overview')}>{tr.overview}</button>
              <button style={tabStyle(tab === 'students')} onClick={() => setTab('students')}>{tr.students}</button>
            </div>

            {tab === 'overview' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 16, marginBottom: 28 }}>
                  {[
                    { label: tr.totalSessions, value: data.stats.total_sessions },
                    { label: tr.weekSessions,  value: data.stats.sessions_this_week },
                    { label: tr.topSubject,    value: data.stats.top_subject || '—', small: true },
                  ].map((s, i) => (
                    <div key={i} style={statCard}>
                      <div style={{ fontSize: s.small ? 18 : 30, fontWeight: 700, color: T.accent }}>{s.value}</div>
                      <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                  <button onClick={() => setShowAdd(true)} style={{ background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', borderRadius: 12, boxShadow: '0 4px 12px rgba(5,150,105,0.3)', padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    + {tr.addStudent}
                  </button>
                </div>

                {data.students.length === 0 ? (
                  <div style={{ color: T.textSecondary, fontSize: 14, textAlign: 'center', padding: '40px 0' }}>{tr.noStudents}</div>
                ) : (
                  <OverviewPanels students={data.students} lang={lang} navigate={navigate} onSetChallenge={setChallengeModal} token={token} />
                )}

                {data.recent_reports.length > 0 && (
                  <div style={card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <div style={{ fontSize: 16, fontWeight: 600, color: T.textPrimary }}>{tr.recentReports}</div>
                      <button onClick={() => navigate('/guardian/reports')} style={{ background: 'none', border: 'none', color: T.accent, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
                        {tr.viewAll} ←
                      </button>
                    </div>
                    {data.recent_reports.map(r => (
                      <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: `1px solid ${T.border}` }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{r.student_name}</div>
                          <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 3 }}>
                            {(REPORT_TYPE_LABEL[lang] || REPORT_TYPE_LABEL.ar)[r.report_type] || r.report_type} · {r.created_at?.slice(0, 10)}
                          </div>
                        </div>
                        <button onClick={() => navigate('/guardian/reports')} style={{ background: 'none', border: 'none', color: T.accent, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                          {tr.viewReport} ←
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {tab === 'students' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                  <button onClick={() => setShowAdd(true)} style={{ background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', borderRadius: 12, boxShadow: '0 4px 12px rgba(5,150,105,0.3)', padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    + {tr.addStudent}
                  </button>
                </div>
                {data.students.length === 0 ? (
                  <div style={{ color: T.textSecondary, fontSize: 14 }}>{tr.noStudents}</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16 }}>
                    {data.students.map(s => <StudentCard key={s.id} student={s} />)}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      {showAdd && (
        <AddStudentModal guardianId={guardianId} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); loadDashboard(); }} />
      )}
      {challengeModal && (
        <ChallengeModal
          student={challengeModal.student}
          lang={lang}
          token={token}
          onClose={() => setChallengeModal(null)}
          onCreated={() => { setChallengeModal(null); loadDashboard(); }}
        />
      )}
      <Toast toast={toast} />
    </div>
  );
}
